import { defineStore } from 'pinia';
import store from '@/store/store';

export const useNotificationsStore = defineStore('notifications', {
  state: () => ({
    userId: store.state.user?.user_id || null,
    notifications: [],
    error: null,
  }),

  getters: {
    unreadNotifications: (state) => state.notifications.filter(n => !n.read),
    allNotifications: (state) => state.notifications,
  },

  actions: {
    getStorageKey() {
      return this.userId ? `notifications_${this.userId}` : null;
    },

    setUserId(userId) {
      this.userId = userId;
      this.loadNotifications();
    },

    reset() {
      this.notifications = [];
      this.error = null;
      this.userId = null;
    },

    resetError() {
      this.error = null;
    },

    setError(err, fallbackMessage, fallbackCode) {
      this.error = {
        message: err?.message || fallbackMessage,
        code: err?.code || fallbackCode,
        timestamp: new Date().toISOString(),
      };
    },

    persistNotifications() {
      const key = this.getStorageKey();
      if (!key) return;
      try {
        localStorage.setItem(key, JSON.stringify(this.notifications));
      } catch (err) {
        this.setError(err, 'Failed to save notifications', 'LOCALSTORAGE_WRITE_FAIL');
      }
    },

    loadNotifications() {
      const key = this.getStorageKey();
      if (!key) {
        this.setError(null, 'No user logged in', 'NO_USER');
        return [];
      }
      try {
        const stored = localStorage.getItem(key);
        this.notifications = stored ? JSON.parse(stored) : [];
        return this.notifications;
      } catch (err) {
        this.setError(err, 'Failed to load notifications', 'LOCALSTORAGE_READ_FAIL');
        return [];
      }
    },

    addNotification(notification) {
      if (!this.userId) {
        this.setError(null, 'No user logged in', 'NO_USER');
        return;
      }
      this.notifications.unshift(notification);
      this.persistNotifications();
    },

    setNotifications(notifications) {
      if (!this.userId) {
        this.setError(null, 'No user logged in', 'NO_USER');
        return;
      }
      this.notifications = notifications;
      this.persistNotifications();
    },

    markAsRead(index) {
      if (!this.userId) {
        this.setError(null, 'No user logged in', 'NO_USER');
        return;
      }
      if (this.notifications[index]) {
        this.notifications[index].read = true;
        this.persistNotifications();
      }
    },

    clearNotifications() {
      if (!this.userId) {
        this.setError(null, 'No user logged in', 'NO_USER');
        return;
      }
      this.notifications = [];
      const key = this.getStorageKey();
      if (key) localStorage.removeItem(key);
    },
  }
});
